/**
 * The main-thread client for the compose worker — and its fallback.
 *
 * WS-J J-17 / D-9. The fallback is NOT optional and is NOT a second implementation: when there is
 * no `Worker` (jsdom, which is why the 107 existing component tests keep passing), when the
 * constructor throws, or when a message errors, this calls `composePixels` INLINE — the same
 * function the worker calls, on the same inputs — logs once per session, and returns identical
 * bytes.
 *
 * The transfer discipline is the other half, and it is the part a future edit is most likely to
 * break:
 *  - every plane crosses by TRANSFER, so nothing is copied;
 *  - `printAlpha`, `sourceRgba` and `despilled` are freshly allocated per compose and the caller is
 *    done with them, so detaching them is safe;
 *  - **`matte` is `.slice()`d before transfer.** The caller RETAINS `matteAlphaRef.current` for the
 *    AI-edge combine and for the uploaded `mattePng`; transferring it would detach that buffer and
 *    blank the persisted matte. `.slice()` on a typed array is the sanctioned copy —
 *    `Array.from` and `[...buf]` are banned (`docs/coding-rules.md`).
 */
import type { AppLogger } from "../app/context.js";
import { composePixels, type ComposePixelInput, type ComposePixelResult } from "./composePipeline.js";

/** Request. Every plane is an ArrayBuffer so it can be TRANSFERRED, never cloned or spread. */
export interface ComposeWorkerRequest {
  id: number;
  width: number;
  height: number;
  printAlpha: ArrayBuffer;
  sourceRgba: ArrayBuffer;
  despilled: ArrayBuffer;
  matte: ArrayBuffer | null;
  feather: number;
  aiEdge: { keep: number } | null;
}

export interface ComposeWorkerResponse {
  id: number;
  ok: true;
  printSolid: ArrayBuffer;
  shellRgba: ArrayBuffer;
  productMask: ArrayBuffer;
  usedAiEdge: boolean;
  aiEdgeSkipped: boolean;
  printCoveragePct: number;
  matteCoveragePct: number;
}

/** Which path produced the last result — the diagnostic line reports it. */
export type ComposePath = "worker" | "fallback";

let nextId = 1;
let worker: Worker | null = null;
/** Null = not tried yet; a string = why the worker is unavailable (logged once). */
let workerUnavailable: string | null = null;
let loggedFallback = false;

/** Test-only: forget the cached worker, the failure reason and the once-per-session log flag. */
export function __resetComposeWorkerForTest(): void {
  try { worker?.terminate(); } catch { /* a stub worker may not implement terminate */ }
  worker = null;
  workerUnavailable = null;
  loggedFallback = false;
  nextId = 1;
}

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (workerUnavailable !== null) return null;
  if (typeof Worker === "undefined") {
    workerUnavailable = "no Worker in this runtime";
    return null;
  }
  try {
    worker = new Worker(new URL("./composeWorker.js", import.meta.url), { type: "module" });
    return worker;
  } catch (e) {
    workerUnavailable = e instanceof Error ? e.message : String(e);
    return null;
  }
}

function noteFallback(logger: AppLogger | undefined): void {
  if (loggedFallback) return;
  loggedFallback = true;
  logger?.log(
    "info",
    `Editor · compose worker unavailable (${workerUnavailable ?? "unknown"}) — running the pixel pass on the main thread`,
  );
}

/** Build the transferable request. `matte` is COPIED; the other three are handed over. */
function toRequest(input: ComposePixelInput, id: number): { req: ComposeWorkerRequest; transfer: ArrayBuffer[] } {
  // `.slice()` — never `Array.from` / spread. See the file header.
  const matteCopy = input.matte ? input.matte.slice() : null;
  const req: ComposeWorkerRequest = {
    id,
    width: input.width,
    height: input.height,
    printAlpha: input.printAlpha.buffer as ArrayBuffer,
    sourceRgba: input.sourceRgba.buffer as ArrayBuffer,
    despilled: input.despilled.buffer as ArrayBuffer,
    matte: matteCopy ? (matteCopy.buffer as ArrayBuffer) : null,
    feather: input.feather,
    aiEdge: input.aiEdge,
  };
  const transfer = [req.printAlpha, req.sourceRgba, req.despilled];
  if (req.matte) transfer.push(req.matte);
  return { req, transfer };
}

function fromResponse(res: ComposeWorkerResponse): ComposePixelResult {
  return {
    printSolid: new Uint8Array(res.printSolid),
    shellRgba: new Uint8ClampedArray(res.shellRgba),
    productMask: new Uint8Array(res.productMask),
    usedAiEdge: res.usedAiEdge,
    aiEdgeSkipped: res.aiEdgeSkipped,
    printCoveragePct: res.printCoveragePct,
    matteCoveragePct: res.matteCoveragePct,
  };
}

/**
 * Run the bake's pixel chain, off the main thread when the runtime allows it.
 *
 * Always resolves with a result — there is no error path that leaves the caller without one, and
 * no path that produces different bytes from the inline one.
 */
export async function runComposePixels(
  input: ComposePixelInput,
  logger?: AppLogger,
): Promise<{ result: ComposePixelResult; path: ComposePath }> {
  const w = ensureWorker();
  if (!w) {
    noteFallback(logger);
    return { result: composePixels(input), path: "fallback" };
  }
  const id = nextId++;
  const { req, transfer } = toRequest(input, id);
  try {
    const res = await new Promise<ComposeWorkerResponse>((resolve, reject) => {
      const onMessage = (e: MessageEvent<ComposeWorkerResponse>) => {
        if (e.data?.id !== id) return;
        cleanup();
        resolve(e.data);
      };
      const onError = (e: unknown) => { cleanup(); reject(e instanceof Error ? e : new Error("compose worker error")); };
      const cleanup = () => {
        w.removeEventListener("message", onMessage as EventListener);
        w.removeEventListener("error", onError as EventListener);
      };
      w.addEventListener("message", onMessage as EventListener);
      w.addEventListener("error", onError as EventListener);
      w.postMessage(req, transfer);
    });
    return { result: fromResponse(res), path: "worker" };
  } catch (e) {
    // The planes were TRANSFERRED, so `input`'s views are detached and cannot be re-run inline.
    // Retire the worker, say why, and let the NEXT compose take the inline path with intact
    // buffers — rather than silently returning a wrong (empty) result for this one.
    workerUnavailable = e instanceof Error ? e.message : String(e);
    try { w.terminate(); } catch { /* best-effort */ }
    worker = null;
    noteFallback(logger);
    throw e instanceof Error ? e : new Error(String(e));
  }
}

/** Re-exported so `composeMockupOnce` can name the pure path in a comment without a second import. */
export { composePixels };
export type { ComposePixelInput, ComposePixelResult };
